import { z } from 'zod';
import { ISODateSchema, ISODateTimeSchema, UUIDSchema } from './common';
import { FileSchema } from './file';
import { AreaValueSchema, PercentageSchema } from './measurements';
import { MonetaryValueSchema, MoneyPerAreaValueSchema, SquareMetreMoneyPerAreaValueSchema } from './money';
import { AppraiserAssignmentSchema } from './appraiser-assignment';
import { RootTenantSummaryItemSchema, TenantSummarySchema, TenantSummaryUsageTypeSchema } from './tenant-summary';
export const SensitivityMatrixSchema = z.object({
    market_values: z.array(MonetaryValueSchema),
    net_yields: z.array(PercentageSchema),
    values: z.array(z.array(MonetaryValueSchema.nullable())),
});
export const ValueCheckStatusSchema = z.enum(['in_progress', 'finalised']);
export const MarketDataMultiplierTypeSchema = z.enum(['multiplier', 'discount_rate']);
export const MarketDataRentValueSchema = z.object({
    base_value: MonetaryValueSchema,
    current_value: MonetaryValueSchema,
});
export const MarketDataLandLabelsSchema = z.object({
    category: z.string().optional(),
    type: z.string().optional(),
    base_value: z.string().optional(),
    current_value: z.string().optional(),
});
export const MarketDataLandValueTypeSchema = z.enum(['number', 'percentage', 'monetary_value', 'money_per_area_value']);
export const MarketDataLandCurrentValueSchema = (valueSchema) => z.object({
    minimum: valueSchema.optional(),
    average: valueSchema.optional(),
    maximum: valueSchema.optional(),
});
const MarketDataLandEntryBaseSchema = z.object({
    category: z.string().optional(),
    type: z.string().optional(),
});
const MarketDataLandNumberEntrySchema = MarketDataLandEntryBaseSchema.extend({
    value_type: z.literal('number').optional(),
    base_value: z.string().optional(),
    current_value: MarketDataLandCurrentValueSchema(z.number()).optional(),
});
const MarketDataLandPercentageEntrySchema = MarketDataLandEntryBaseSchema.extend({
    value_type: z.literal('percentage'),
    base_value: z.string().optional(),
    current_value: MarketDataLandCurrentValueSchema(z.number()).optional(),
});
const MarketDataLandMonetaryEntrySchema = MarketDataLandEntryBaseSchema.extend({
    value_type: z.literal('monetary_value'),
    base_value: z.string().optional(),
    current_value: MarketDataLandCurrentValueSchema(MonetaryValueSchema).optional(),
});
const MarketDataLandMoneyPerAreaEntrySchema = MarketDataLandEntryBaseSchema.extend({
    value_type: z.literal('money_per_area_value'),
    base_value: z.string().optional(),
    current_value: MarketDataLandCurrentValueSchema(SquareMetreMoneyPerAreaValueSchema).optional(),
});
export const MarketDataLandEntrySchema = z.union([
    MarketDataLandNumberEntrySchema,
    MarketDataLandPercentageEntrySchema,
    MarketDataLandMonetaryEntrySchema,
    MarketDataLandMoneyPerAreaEntrySchema,
]);
export const MarketDataLandSchema = z.object({
    labels: MarketDataLandLabelsSchema.optional(),
    entries: z.array(MarketDataLandEntrySchema),
});
export const MarketDataRentSchema = z.object({
    usage_type: TenantSummaryUsageTypeSchema,
    average: MarketDataRentValueSchema.optional(),
    minimum: MarketDataRentValueSchema.optional(),
    maximum: MarketDataRentValueSchema.optional(),
});
export const MarketDataMultiplierValueSchema = z.object({
    base_value: z.number(),
    current_value: z.number(),
});
export const MarketDataMultiplierSchema = z.object({
    multiplier_type: MarketDataMultiplierTypeSchema,
    average: MarketDataMultiplierValueSchema.optional(),
    minimum: MarketDataMultiplierValueSchema.optional(),
    maximum: MarketDataMultiplierValueSchema.optional(),
});
export const MarketDataSchema = z.object({
    rents: z.array(MarketDataRentSchema),
    multiplier: MarketDataMultiplierSchema.optional(),
    land: MarketDataLandSchema.optional(),
});
export const MarketDataRentValueReadSchema = MarketDataRentValueSchema.extend({
    difference: PercentageSchema,
});
export const MarketDataRentReadSchema = MarketDataRentSchema.extend({
    average: MarketDataRentValueReadSchema.optional(),
    minimum: MarketDataRentValueReadSchema.optional(),
    maximum: MarketDataRentValueReadSchema.optional(),
});
export const MarketDataMultiplierValueReadSchema = MarketDataMultiplierValueSchema.extend({
    difference: PercentageSchema,
});
export const MarketDataMultiplierReadSchema = MarketDataMultiplierSchema.extend({
    average: MarketDataMultiplierValueReadSchema.optional(),
    minimum: MarketDataMultiplierValueReadSchema.optional(),
    maximum: MarketDataMultiplierValueReadSchema.optional(),
});
export const MarketDataReadSchema = z.object({
    rents: z.array(MarketDataRentReadSchema),
    multiplier: MarketDataMultiplierReadSchema.optional(),
    land: MarketDataLandSchema.optional(),
});
const ValueCheckReportSchema = FileSchema.pick({ name: true, category: true, url: true }).extend({
    uuid: UUIDSchema,
    content_type: z.string(),
});
export const ValueCheckReportFileSchema = ValueCheckReportSchema;
export const ValueCheckReportXlsSchema = ValueCheckReportSchema;
export const ValueCheckRentalDevelopmentImageSchema = ValueCheckReportSchema.extend({
    thumbnail_url: z.string().optional(),
});
export const UpdateValueCheckSchema = z.object({
    order_uuid: UUIDSchema.optional(),
    appraiser_assignment: AppraiserAssignmentSchema.optional(),
    valuation_date: ISODateSchema.optional(),
    report_date: ISODateSchema.optional(),
    market_value: MonetaryValueSchema,
    confirmed: z.boolean().optional(),
    land_value: MonetaryValueSchema.optional(),
    income_market_value: MonetaryValueSchema.optional(),
    discount_rate: PercentageSchema.optional(),
    net_income_construction: MonetaryValueSchema.optional(),
    value_construction: MonetaryValueSchema.optional(),
    total_special_property_features: MonetaryValueSchema.optional(),
    gross_capital_value: MonetaryValueSchema.optional(),
    net_capital_value: MonetaryValueSchema.optional(),
    ancillary_acquisition_costs: MonetaryValueSchema.optional(),
    net_yield: PercentageSchema.optional(),
    gross_yield: PercentageSchema.optional(),
    gross_yield_multiplier: z.number().optional(),
    net_yield_multiplier: z.number().optional(),
    tenant_summary: TenantSummarySchema.optional(),
    transaction_costs: MonetaryValueSchema.optional(),
    gross_floor_area: AreaValueSchema.optional(),
    sensitivity_matrix: SensitivityMatrixSchema.optional(),
    tenant_summary_items: z.array(RootTenantSummaryItemSchema).optional(),
    market_data: MarketDataSchema.optional(),
});
const ReadValueCheckFieldsSchema = z.object({
    uuid: UUIDSchema,
    asset_uuid: UUIDSchema,
    status: ValueCheckStatusSchema,
    base_valuation_uuid: UUIDSchema,
    report_file: ValueCheckReportFileSchema.optional(),
    report_xls: ValueCheckReportXlsSchema.optional(),
    rental_development_image: ValueCheckRentalDevelopmentImageSchema.optional(),
    delta_market_value: PercentageSchema.optional(),
    market_value_including_transaction_costs: MonetaryValueSchema,
    market_value_per_gross_floor_area: MoneyPerAreaValueSchema.optional(),
    market_value_per_rentable_area: MoneyPerAreaValueSchema.optional(),
    market_data: MarketDataReadSchema.optional(),
    created_on: ISODateTimeSchema.optional(),
    updated_on: ISODateTimeSchema.optional(),
});
export const ReadValueCheckSchema = ReadValueCheckFieldsSchema.and(UpdateValueCheckSchema);
export const ValueCheckListSchema = z.object({
    value_checks: z.array(ReadValueCheckSchema),
});
